/**
 * CEX token-groups, exchanges, accounts, algorithms, bots. Everything is
 * fetched from the CEX control-plane when the platform is up (`showCex`) **and**
 * the user may use CEX token groups (`canAccessCexTokens` from `/api/users/me`,
 * with legacy fallback to `canAccessCex` if the field is absent). CEO always loads.
 * Otherwise we skip fetches to avoid 401 noise for DEX-only users.
 *
 * On refresh, a failed endpoint does not clear prior data (no silent "empty" list
 * on 401/503). See `loadErrors` for per-slice messages.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { cexJson, formatCexUserError, isCexAvailabilityError } from '../lib/cexApi.js'
import { subscribeAmbient } from '../hooks/cexAmbientHub.js'
import { useAuth } from './AuthContext.jsx'
import { usePlatform } from './PlatformContext.jsx'

/**
 * Freshness window for cached balances. Shorter than the normal poll cadence
 * so we don't show stale numbers for long, long enough that flipping between
 * pairs in the same group is instant.
 */
const BALANCE_CACHE_TTL_MS = 30_000
/** Fallback when WS/NATS misses `cex.bots.changed` / `cex.token_groups.changed` (other browser, second tab). */
const CEX_META_POLL_MS = 3_000
const CEX_META_FOCUS_THROTTLE_MS = 1_200

const CexContext = createContext(null)

const KEYS = [
  { key: 'tokenGroups', path: '/api/cex/token-groups' },
  { key: 'exchanges', path: '/api/cex/exchanges' },
  { key: 'accounts', path: '/api/cex/accounts' },
  { key: 'algorithms', path: '/api/cex/algorithms' },
  { key: 'bots', path: '/api/cex/bots' },
]

function _normArray(v) {
  return Array.isArray(v) ? v : []
}

function _errMessage(e) {
  if (e && typeof e === 'object' && e.message) return String(e.message)
  return String(e)
}

function _friendlyRejection(e) {
  if (isCexAvailabilityError(e)) return ''
  if (e && typeof e === 'object' && e.name === 'CexHttpError') {
    return formatCexUserError(e)
  }
  return _errMessage(e)
}

export function CexProvider({ children }) {
  const { showCex } = usePlatform()
  const { isCeo, canAccessCex, user } = useAuth()
  const mayLoadCex = useMemo(() => {
    if (!showCex) return false
    if (isCeo) return true
    if (user?.canAccessCexTokens === false) return false
    if (user?.canAccessCexTokens === true) return true
    // Older sessions before `canAccessCexTokens` was added to /api/users/me
    return canAccessCex
  }, [showCex, isCeo, user, canAccessCex])
  const [tokenGroups, setTokenGroups] = useState([])
  const [exchanges, setExchanges] = useState([])
  const [accounts, setAccounts] = useState([])
  const [algorithms, setAlgorithms] = useState([])
  const [bots, setBots] = useState([])
  const [loading, setLoading] = useState(false)
  /** First error in the fixed KEY order (for a single line / legacy) */
  const [error, setError] = useState(null)
  /** Per-slice: failed fetches; cleared for a slice on the next success. */
  const [loadErrors, setLoadErrors] = useState(
    () => /** @type {Record<string, string>} */ ({}),
  )
  // Per-account balance snapshots: { [accountId]: { assets, ts } }. Mutable
  // ref + a setter so consumers see updates without a re-render when they only
  // read the cache imperatively. Kept in memory only — it's small and tied to
  // the session.
  const balancesCacheRef = useRef(
    /** @type {Record<string, { assets: Array, ts: number }>} */ ({}),
  )
  const [, setBalancesTick] = useState(0)

  const getCachedBalance = useCallback((accountId) => {
    if (!accountId) return null
    const entry = balancesCacheRef.current[accountId]
    if (!entry) return null
    if (Date.now() - entry.ts > BALANCE_CACHE_TTL_MS) return null
    return entry
  }, [])

  const setCachedBalance = useCallback((accountId, payload) => {
    if (!accountId || !payload || !Array.isArray(payload.assets)) return
    balancesCacheRef.current = {
      ...balancesCacheRef.current,
      [accountId]: { assets: payload.assets, ts: Date.now() },
    }
    setBalancesTick((n) => n + 1)
  }, [])

  const setters = useMemo(
    () => ({
      tokenGroups: setTokenGroups,
      exchanges: setExchanges,
      accounts: setAccounts,
      algorithms: setAlgorithms,
      bots: setBots,
    }),
    [],
  )

  const refresh = useCallback(async () => {
    if (!mayLoadCex) return
    setLoading(true)
    setError(null)
    try {
      const settled = await Promise.allSettled(KEYS.map(({ path }) => cexJson(path)))

      for (let i = 0; i < settled.length; i++) {
        const { key } = KEYS[i]
        const result = settled[i]
        if (result.status === 'fulfilled') {
          setters[key](_normArray(result.value))
        }
      }

      setLoadErrors((prev) => {
        const n = { ...prev }
        for (let i = 0; i < settled.length; i++) {
          const { key } = KEYS[i]
          const r = settled[i]
          if (r.status === 'fulfilled') {
            delete n[key]
          } else {
            const fr = _friendlyRejection(r.reason)
            if (fr) n[key] = fr
            else delete n[key]
          }
        }
        return n
      })

      let first = null
      for (let i = 0; i < settled.length; i++) {
        if (settled[i].status === 'rejected') {
          const msg = _friendlyRejection(settled[i].reason)
          if (msg) {
            first = msg
            break
          }
        }
      }
      setError(first)
    } catch (e) {
      if (isCexAvailabilityError(e)) {
        setError(null)
      } else {
        setError(e && typeof e === 'object' && e.name === 'CexHttpError' ? formatCexUserError(e) : _errMessage(e))
      }
    } finally {
      setLoading(false)
    }
  }, [mayLoadCex, setters])

  useEffect(() => {
    if (!mayLoadCex) {
      // Keep last-known lists if platform health flaps — avoids empty flash; refresh runs when CEX is back.
      setLoading(false)
      return
    }
    let cancelled = false
    const run = () => {
      if (cancelled) return
      void refresh()
    }
    let cancelSchedule
    if (typeof requestIdleCallback !== 'undefined' && typeof cancelIdleCallback === 'function') {
      const id = requestIdleCallback(run, { timeout: 3000 })
      cancelSchedule = () => {
        try {
          cancelIdleCallback(id)
        } catch {
          /* noop */
        }
      }
    } else {
      const id = setTimeout(run, 0)
      cancelSchedule = () => clearTimeout(id)
    }
    return () => {
      cancelled = true
      cancelSchedule()
    }
  }, [mayLoadCex, refresh])

  // Realtime fan-out: token-group and bot-registry mutations publish ambient WS
  // events (`cex.token_groups.changed`, `cex.bots.changed`). We coalesce bursts
  // with a 150 ms trailing debounce so several store writes in one UX action
  // do not hammer the list endpoints.
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh
  useEffect(() => {
    if (!mayLoadCex) return undefined
    let debounce = null
    const schedule = () => {
      if (debounce) clearTimeout(debounce)
      debounce = setTimeout(() => {
        debounce = null
        try {
          void refreshRef.current?.()
        } catch {
          /* ignore */
        }
      }, 150)
    }
    const u1 = subscribeAmbient('cex.token_groups.changed', schedule)
    const u2 = subscribeAmbient('cex.bots.changed', schedule)
    return () => {
      if (debounce) clearTimeout(debounce)
      u1()
      u2()
    }
  }, [mayLoadCex])

  // After WS (re)connect, pull latest lists in case we missed `cex.bots.changed` (NATS down, race).
  useEffect(() => {
    if (!mayLoadCex) return undefined
    if (typeof window === 'undefined') return undefined
    const onWs = () => {
      try {
        void refreshRef.current?.()
      } catch {
        /* ignore */
      }
    }
    window.addEventListener('lotos:cex-ambient-ws-open', onWs)
    return () => window.removeEventListener('lotos:cex-ambient-ws-open', onWs)
  }, [mayLoadCex])

  // Safety net: if the WS drops and we miss an event, catch up on tab focus / window focus
  // and periodic `meta` compare (other browser, background tab, bfcache).
  // `token-groups/meta` and `bots/meta` are cheap; RBAC applies where relevant.
  useEffect(() => {
    if (!mayLoadCex) return undefined
    if (typeof document === 'undefined') return undefined
    let lastTgRev = null
    let lastBotsRev = null
    let lastFocusProbe = 0
    let cancelled = false
    const probe = async () => {
      if (cancelled) return
      let need = false
      try {
        const tg = await cexJson('/api/cex/token-groups/meta')
        if (tg && typeof tg.rev === 'number') {
          if (lastTgRev !== null && tg.rev !== lastTgRev) need = true
          lastTgRev = tg.rev
        }
      } catch {
        /* older CP without meta — ignore */
      }
      try {
        const botsMeta = await cexJson('/api/cex/bots/meta')
        if (botsMeta && typeof botsMeta.rev === 'number') {
          if (lastBotsRev !== null && botsMeta.rev !== lastBotsRev) need = true
          lastBotsRev = botsMeta.rev
        }
      } catch {
        /* older CP or transient */
      }
      if (need) void refreshRef.current?.()
    }
    const onVis = () => {
      if (document.visibilityState === 'visible') void probe()
    }
    const onWinFocus = () => {
      const t = Date.now()
      if (t - lastFocusProbe < CEX_META_FOCUS_THROTTLE_MS) return
      lastFocusProbe = t
      void probe()
    }
    const onPageShow = (e) => {
      if (e.persisted) void probe()
    }
    document.addEventListener('visibilitychange', onVis)
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onWinFocus)
      window.addEventListener('pageshow', onPageShow)
    }
    void probe()
    const pollId =
      typeof window !== 'undefined'
        ? window.setInterval(() => {
            if (cancelled) return
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
            void probe()
          }, CEX_META_POLL_MS)
        : null
    return () => {
      cancelled = true
      if (pollId != null) window.clearInterval(pollId)
      document.removeEventListener('visibilitychange', onVis)
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onWinFocus)
        window.removeEventListener('pageshow', onPageShow)
      }
    }
  }, [mayLoadCex])

  return (
    <CexContext.Provider
      value={{
        tokenGroups,
        exchanges,
        accounts,
        algorithms,
        bots,
        loading,
        error,
        loadErrors,
        refresh,
        getCachedBalance,
        setCachedBalance,
      }}
    >
      {children}
    </CexContext.Provider>
  )
}

export function useCex() {
  const ctx = useContext(CexContext)
  if (!ctx) throw new Error('useCex must be used within CexProvider')
  return ctx
}
